import { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent } from '@mui/material';
import { Line, Pie, Bar, Doughnut } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';
import { request } from '../../../../utils';
import PatientsIcon from '@mui/icons-material/Person';
import AppointmentIcon from '@mui/icons-material/Event';
import DoctorIcon from '@mui/icons-material/LocalHospital';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, BarElement);

export const HMS = () => {
  const [dailyPatients, setDailyPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [pieChartData, setPieChartData] = useState({ labels: [], datasets: [] });
  const [monthlyData, setMonthlyData] = useState({ labels: [], datasets: [] });
  const [totalCashRevenue, setTotalCashRevenue] = useState(0); // New state for cash revenue

  useEffect(() => {
    const fetchDailyPatients = async () => {
      setLoading(true);
      try {
        const response = await request('/patient-registeration-list', { method: 'GET' });

        if (response?.success) {
          const allPatients = response.patientData;
          const today = new Date();

          const todayPatients = allPatients.filter((patient) => {
            const createdAtDate = new Date(patient.createdAt);
            return (
              createdAtDate.getDate() === today.getDate() &&
              createdAtDate.getMonth() === today.getMonth() &&
              createdAtDate.getFullYear() === today.getFullYear()
            );
          });

          // Calculate total cash revenue for today using the 'recd' field
          const cashRevenue = todayPatients.reduce((total, patient) => {
            return total + (patient.receipt_mode === 'CASH' ? patient.recd : 0); // Use 'recd' field
          }, 0);

          setTotalCashRevenue(cashRevenue); // Update state with total cash revenue

          // Weekly data for the line chart
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());

          const dailyCounts = Array(7).fill(0);
          allPatients.forEach((patient) => {
            const createdAtDate = new Date(patient.createdAt);
            if (createdAtDate >= startOfWeek && createdAtDate <= today) {
              const dayIndex = createdAtDate.getDay();
              dailyCounts[dayIndex]++;
            }
          });

          setDailyPatients(todayPatients);
          setChartData({
            labels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            datasets: [
              {
                label: 'Patients',
                data: dailyCounts,
                backgroundColor: 'rgba(75,192,192,0.3)',
                borderColor: 'rgba(75,192,192,0.7)',
                borderWidth: 1.5
              }
            ]
          });

          // Data for the pie chart
          const receiptModeCounts = todayPatients.reduce((acc, patient) => {
            acc[patient.receipt_mode] = (acc[patient.receipt_mode] || 0) + 1;
            return acc;
          }, {});

          setPieChartData({
            labels: Object.keys(receiptModeCounts),
            datasets: [
              {
                data: Object.values(receiptModeCounts),
                backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
                borderWidth: 1
              }
            ]
          });

          // Monthly data for the bar chart
          const monthlyCounts = Array(12).fill(0);
          allPatients.forEach((patient) => {
            const createdAtDate = new Date(patient.createdAt);
            if (createdAtDate.getFullYear() === today.getFullYear()) {
              const monthIndex = createdAtDate.getMonth();
              monthlyCounts[monthIndex]++;
            }
          });

          setMonthlyData({
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            datasets: [
              {
                label: 'Patients per Month',
                data: monthlyCounts,
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1.5
              }
            ]
          });
        } else {
          toast('Failed to load patient details', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching patient registrations:', error);
        toast('Error fetching patient registrations', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchDailyPatients();
  }, []);

  return (
    <Box sx={{ flex: 1, p: 2, background: 'linear-gradient(to right, #e0f7fa, #f4f6f8)' }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4ca1af, #c4e0e5)',
              border: '0.5px solid #ccc',
              borderRadius: '12px',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
              transition: '0.3s',
              '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' }
            }}
          >
            <CardContent>
              <PatientsIcon sx={{ fontSize: 60, color: 'white', borderRadius: '8px' }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                Total Patients Today
              </Typography>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                {dailyPatients.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4ca1af, #c4e0e5)',
              border: '0.5px solid #ccc',
              borderRadius: '12px',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
              transition: '0.3s',
              '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' }
            }}
          >
            <CardContent>
              <AppointmentIcon sx={{ fontSize: 60, color: 'white', borderRadius: '8px' }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                Appointments
              </Typography>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                1
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4ca1af, #c4e0e5)',
              borderRadius: '12px',
              border: '0.5px solid #ccc',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
              transition: '0.3s',
              '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' }
            }}
          >
            <CardContent>
              <DoctorIcon sx={{ fontSize: 60, color: 'white', borderRadius: '8px' }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                Total Doctors
              </Typography>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                1
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* New Card for Total Cash Revenue */}
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4ca1af, #c4e0e5)',
              borderRadius: '12px',
              border: '0.5px solid #ccc',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
              transition: '0.3s',
              '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' }
            }}
          >
            <CardContent>
              <CurrencyRupeeIcon sx={{ fontSize: 60, color: 'white', borderRadius: '8px' }} />
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                Total Cash Revenue
              </Typography>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                ₹{totalCashRevenue.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transition: '0.3s',
                  '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }
                }}
              >
                <CardContent sx={{ p: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.4rem' }}>
                    Daily Patients
                  </Typography>
                  <Box sx={{ height: '300px', position: 'relative' }}>
                    <Line data={chartData} options={{ responsive: true, maintainAspectRatio: true }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transition: '0.3s',
                  '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }
                }}
              >
                <CardContent sx={{ p: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.4rem' }}>
                    Receipt Mode Distribution
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '285px',
                      mx: 'auto'
                    }}
                  >
                    <Pie data={pieChartData} options={{ maintainAspectRatio: true }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transition: '0.3s',
                  '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }
                }}
              >
                <CardContent sx={{ p: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.4rem' }}>
                    Monthly Patients
                  </Typography>
                  <Box sx={{ height: '300px', position: 'relative' }}>
                    <Bar data={monthlyData} options={{ responsive: true, maintainAspectRatio: true }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
